/**
 * Custom Size Modal Component
 * Handles multi-step custom dress measurement collection
 */

if (!customElements.get("m-custom-size")) {
  class CustomSizeModal extends HTMLElement {
    constructor() {
      super();
      
      this.currentStep = 1;
      this.totalSteps = 4;
      this.isClosing = false;
      this.measurements = {
        bust: 90,
        waist: 70,
        hips: 95,
        'skirt-length': 100
      };
      
      this.selectors = {
        openButton: '[data-open-custom-size]',
        modalContent: '[data-custom-size-modal]',
        steps: '[data-step]',
        backButton: '[data-custom-size-back]',
        continueButton: '[data-custom-size-continue]',
        completeButton: '[data-custom-size-complete]',
        rangeInputs: '[data-measurement]',
        numberInputs: '[data-measurement-input]',
        progressFill: '[data-progress-fill]',
        progressText: '[data-progress-text]'
      };
    }

    connectedCallback() {
      console.log('✅ CustomSizeModal instance initialized');
      const instances = document.querySelectorAll('m-custom-size').length;
      console.log(`  → Total instances on page: ${instances}`);
      if (instances > 1) {
        console.warn('⚠️ WARNING: Multiple m-custom-size instances detected! This will cause duplicate event listeners.');
      }
      this.init();
    }

    init() {
      this.modal = null;
      this.setupEventListeners();
    }

    setupEventListeners() {
      // Open modal button - use event delegation from document
      if (typeof addEventDelegate === 'function') {
        addEventDelegate({
          selector: this.selectors.openButton,
          handler: (e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('Open button clicked, isClosing:', this.isClosing);
            if (!this.isClosing) {
              this.openModal();
            }
          }
        });
      } else {
        // Fallback: direct event listener
        document.addEventListener('click', (e) => {
          const button = e.target.closest(this.selectors.openButton);
          if (button) {
            e.preventDefault();
            e.stopPropagation();
            console.log('Open button clicked (fallback), isClosing:', this.isClosing);
            if (!this.isClosing) {
              this.openModal();
            }
          }
        });
      }
    }

    openModal() {
      if (this.isClosing) {
        console.log('⚠️ Modal is closing, ignoring open request');
        console.trace('Called from:'); // Show who called it
        return;
      }
      
      console.log('🔵 Opening custom size modal...');
      console.trace('Called from:'); // Show call stack
      
      // Always create a fresh modal instance
      this.modal = new MinimogTheme.Modal();
      console.log('  → Created new modal instance');

      const modalContent = this.querySelector(this.selectors.modalContent);
      if (!modalContent) {
        console.error('Custom size modal content not found');
        return;
      }

      const colorScheme = modalContent.dataset.colorScheme || '';
      
      // Clone the modal content
      const modalClone = modalContent.cloneNode(true);
      console.log('  → Cloned modal content');
      
      // Make clone visible and reset any hiding styles
      modalClone.style.display = 'block';
      modalClone.style.visibility = 'visible';
      modalClone.style.position = 'relative';
      modalClone.style.left = 'auto';
      
      // Reset measurements to default values
      this.resetMeasurements(modalClone);
      console.log('  → Reset measurements to defaults');
      
      // Reset to first step
      this.currentStep = 1;
      this.updateStepDisplay(modalClone);
      console.log('  → Reset to step 1');
      
      // Set up modal
      this.modal.appendChild(modalClone);
      this.modal.setWidth('95%');
      
      // Build class string without empty tokens
      const modalClasses = ['m-custom-size-modal', 'm-gradient', colorScheme].filter(c => c).join(' ');
      this.modal.setSizes(modalClasses);
      console.log('  → Set up modal');
      
      // Prevent closing on background click
      if (this.modal.modal) {
        this.modal.modal.setAttribute('data-prevent-close', 'true');
      }
      
      // Set up event listeners within modal
      this.setupModalEventListeners(modalClone);
      console.log('  → Set up event listeners');
      
      // Select the Custom Size radio button
      this.selectCustomSizeButton();
      console.log('  → Selected custom size button');
      
      // Open modal
      this.modal.open();
      console.log('✅ Modal opened successfully');
      
      // Prevent background click from closing
      setTimeout(() => {
        const modalWrapper = document.querySelector('.m-modal--wrapper');
        if (modalWrapper) {
          const preventClose = (e) => {
            if (e.target === modalWrapper) {
              e.stopPropagation();
              e.preventDefault();
              console.log('Background click prevented');
            }
          };
          modalWrapper.addEventListener('click', preventClose);
        }
      }, 100);
      
      // Focus first input
      setTimeout(() => {
        const firstRange = modalClone.querySelector(`[data-step="1"] ${this.selectors.rangeInputs}`);
        if (firstRange) firstRange.focus();
      }, 300);
    }
    
    selectCustomSizeButton() {
      console.log('Selecting custom size button');
      
      // Find and check the custom size radio button
      const customSizeRadio = document.querySelector('[data-open-custom-size-trigger]');
      if (customSizeRadio) {
        customSizeRadio.checked = true;
        
        // Uncheck other size options
        const sizeRadios = document.querySelectorAll('.m-product-option input[type="radio"]');
        sizeRadios.forEach(radio => {
          if (radio !== customSizeRadio) {
            radio.checked = false;
          }
        });
        
        console.log('  → Custom size button selected');
      }
    }

    setupModalEventListeners(modalElement) {
      // Range slider inputs
      modalElement.querySelectorAll(this.selectors.rangeInputs).forEach(range => {
        const measurement = range.dataset.measurement;
        const numberInput = modalElement.querySelector(`[data-measurement-input="${measurement}"]`);
        
        if (!numberInput) return;
        
        // Sync range to number input
        range.addEventListener('input', (e) => {
          const value = e.target.value;
          numberInput.value = value;
          this.measurements[measurement] = parseInt(value);
        });
        
        // Sync number input to range
        numberInput.addEventListener('input', (e) => {
          const inputValue = e.target.value;
          
          // Allow empty input while typing
          if (inputValue === '') {
            return;
          }
          
          let value = parseInt(inputValue);
          const min = parseInt(numberInput.min) || 1;
          const max = parseInt(numberInput.max) || 250;
          
          // Only update if valid number
          if (!isNaN(value)) {
            // Clamp value
            if (value < min) value = min;
            if (value > max) value = max;
            
            range.value = value;
            this.measurements[measurement] = value;
          }
        });
        
        // Validate on blur (when user leaves field)
        numberInput.addEventListener('blur', (e) => {
          let value = parseInt(e.target.value);
          const min = parseInt(numberInput.min) || 1;
          const max = parseInt(numberInput.max) || 250;
          
          if (isNaN(value) || value < min) {
            value = min;
          } else if (value > max) {
            value = max;
          }
          
          numberInput.value = value;
          range.value = value;
          this.measurements[measurement] = value;
        });
      });

      // Back buttons
      modalElement.querySelectorAll(this.selectors.backButton).forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('Back clicked, current step:', this.currentStep);
          if (this.currentStep > 1) {
            this.currentStep--;
            this.updateStepDisplay(modalElement);
          }
        });
      });

      // Continue buttons
      modalElement.querySelectorAll(this.selectors.continueButton).forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('Continue clicked, current step:', this.currentStep);
          if (this.currentStep < this.totalSteps) {
            this.currentStep++;
            this.updateStepDisplay(modalElement);
          }
        });
      });

      // Complete button
      modalElement.querySelectorAll(this.selectors.completeButton).forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('Complete clicked');
          this.completeMeasurements(modalElement);
        });
      });

      // Keyboard navigation
      modalElement.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (this.currentStep < this.totalSteps) {
            this.currentStep++;
            this.updateStepDisplay(modalElement);
          } else {
            this.completeMeasurements();
          }
        } else if (e.key === 'Escape') {
          this.modal?.close();
        }
      });
    }

    updateStepDisplay(modalElement) {
      // Update step visibility
      modalElement.querySelectorAll(this.selectors.steps).forEach(step => {
        const stepNumber = parseInt(step.dataset.step);
        if (stepNumber === this.currentStep) {
          step.setAttribute('data-step-active', '');
        } else {
          step.removeAttribute('data-step-active');
        }
      });

      // Update progress bar
      const progressFill = modalElement.querySelector(this.selectors.progressFill);
      const progressText = modalElement.querySelector(this.selectors.progressText);
      
      if (progressFill) {
        const progress = (this.currentStep / this.totalSteps) * 100;
        progressFill.style.width = `${progress}%`;
      }
      
      if (progressText) {
        progressText.textContent = `Step ${this.currentStep} of ${this.totalSteps}`;
      }

      // Update back button state
      modalElement.querySelectorAll(this.selectors.backButton).forEach(btn => {
        const step = btn.closest('[data-step]');
        const stepNumber = parseInt(step.dataset.step);
        btn.disabled = stepNumber === 1;
      });

      // Focus on the current step's range input
      setTimeout(() => {
        const currentStepElement = modalElement.querySelector(`[data-step="${this.currentStep}"]`);
        const rangeInput = currentStepElement?.querySelector(this.selectors.rangeInputs);
        if (rangeInput) rangeInput.focus();
      }, 100);
    }

    resetMeasurements(modalElement) {
      // Reset measurements object to default values
      const defaults = {
        bust: 90,
        waist: 70,
        hips: 95,
        'skirt-length': 100
      };
      
      this.measurements = { ...defaults };
      console.log('  → Measurements reset to:', this.measurements);
      
      // Reset all input fields in the modal to default values
      Object.keys(defaults).forEach(measurement => {
        const rangeInput = modalElement.querySelector(`[data-measurement="${measurement}"]`);
        const numberInput = modalElement.querySelector(`[data-measurement-input="${measurement}"]`);
        
        if (rangeInput) {
          rangeInput.value = defaults[measurement];
        }
        if (numberInput) {
          numberInput.value = defaults[measurement];
        }
      });
      
      console.log('  → All input fields reset to default values');
    }

    completeMeasurements(modalElement) {
      console.log('✅ COMPLETE BUTTON CLICKED - Starting close process');
      console.log('Measurements:', this.measurements);
      
      // CLOSE THE CUSTOM SIZE MODAL - Disable buttons approach
      console.log('🔴 CLOSING CUSTOM SIZE MODAL');
      this.isClosing = true;
      console.log('  → Set isClosing = true');
      
      // Physically disable all open buttons during close
      const openButtons = document.querySelectorAll('[data-open-custom-size]');
      console.log('  → Found', openButtons.length, 'open buttons, disabling them');
      openButtons.forEach(btn => {
        btn.style.pointerEvents = 'none';
        btn.style.opacity = '0.5';
        btn.setAttribute('disabled', 'disabled');
      });
      
      // Use the modal instance's close method
      if (this.modal && typeof this.modal.close === 'function') {
        console.log('  → Calling modal.close()');
        this.modal.close();
        
        // Re-enable buttons and destroy modal instance after delay
        setTimeout(() => {
          console.log('  → Re-enabling buttons');
          openButtons.forEach(btn => {
            btn.style.pointerEvents = '';
            btn.style.opacity = '';
            btn.removeAttribute('disabled');
          });
          this.modal = null;
          this.isClosing = false;
          console.log('  → Modal instance destroyed, isClosing = false, ready for reopen');
        }, 2000);
      } else {
        this.isClosing = false;
        openButtons.forEach(btn => {
          btn.style.pointerEvents = '';
          btn.style.opacity = '';
          btn.removeAttribute('disabled');
        });
      }
      
      console.log('✅ CUSTOM SIZE MODAL CLOSE CALLED');
      
      // Then do other stuff (even if they error, modal is already closed)
      try {
        // Create custom size string with 4 measurements
        const customSizeString = `Custom (B:${this.measurements.bust} W:${this.measurements.waist} H:${this.measurements.hips} L:${this.measurements['skirt-length']})`;
        
        // Update the size selection in the product form
        this.updateProductSize(customSizeString);
        
        // DON'T call selectCustomSizeButton() - it can trigger reopening
        // The summary display is enough to show custom size was selected
        console.log('  → Skipping button selection to prevent reopen');
        
        // Show success notification
        if (window.MinimogTheme && MinimogTheme.Notification) {
          MinimogTheme.Notification.show({
            target: document.body,
            method: 'appendChild',
            type: 'success',
            message: 'Custom measurements saved successfully!',
            delay: 3000
          });
        }
      } catch (error) {
        console.error('Error in completeMeasurements (but modal already closed):', error);
      }
    }
    
    forceCloseModal() {
      // Remove all modal-related classes and elements immediately
      console.log('Step 1: Removing modal classes from body');
      document.body.classList.remove('m-modal--open', 'modal-open', 'overflow-hidden');
      document.documentElement.classList.remove('m-modal--open', 'modal-open');
      
      console.log('Step 2: Restoring body scroll');
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.documentElement.style.overflow = '';
      
      console.log('Step 3: Finding and hiding modal wrapper');
      const modalWrappers = document.querySelectorAll('.m-modal--wrapper, .m-modal, [class*="modal"]');
      modalWrappers.forEach((wrapper, index) => {
        console.log(`  Hiding wrapper ${index}:`, wrapper.className);
        wrapper.classList.remove('m-modal--open', 'active', 'show');
        wrapper.style.display = 'none';
        wrapper.style.opacity = '0';
        wrapper.style.visibility = 'hidden';
        wrapper.style.pointerEvents = 'none';
      });
      
      console.log('Step 4: Removing backdrop');
      const backdrops = document.querySelectorAll('.m-modal--backdrop, .modal-backdrop, [class*="backdrop"]');
      backdrops.forEach((backdrop, index) => {
        console.log(`  Removing backdrop ${index}`);
        backdrop.remove();
      });
      
      console.log('Step 5: Hiding modal content');
      const modalContents = document.querySelectorAll('.m-modal--content, .modal-content');
      modalContents.forEach((content, index) => {
        console.log(`  Hiding content ${index}`);
        content.style.display = 'none';
      });
      
      // Try modal.close() as well
      if (this.modal && typeof this.modal.close === 'function') {
        console.log('Step 6: Calling modal.close() method');
        try {
          this.modal.close();
        } catch (e) {
          console.error('Error calling modal.close():', e);
        }
      }
      
      console.log('✅ MODAL CLOSE COMPLETE');
    }

    updateProductSize(customSizeString) {
      // Find the custom size button and mark it as selected
      const customSizeButton = document.querySelector('[data-open-custom-size]');
      if (customSizeButton) {
        customSizeButton.setAttribute('data-selected', '');
        customSizeButton.setAttribute('data-custom-measurements', JSON.stringify(this.measurements));
        
        // Update button text
        const buttonText = customSizeButton.querySelector('.m-custom-size-button__text');
        if (buttonText) {
          buttonText.textContent = 'Custom';
        }
        
        // Add checkmark badge
        let badge = customSizeButton.querySelector('.m-custom-size-button__badge');
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'm-custom-size-button__badge';
          badge.innerHTML = '✓';
          customSizeButton.appendChild(badge);
        }
      }

      // Update or create summary element
      this.showCustomSizeSummary();

      // Store measurements in a hidden input for form submission
      this.storeCustomSizeData(customSizeString);

      // Trigger variant change if needed
      if (window.MinimogEvents && window.MinimogTheme) {
        MinimogEvents.emit(MinimogTheme.pubSubEvents.optionValueSelectionChange, {
          data: {
            customSize: true,
            measurements: this.measurements
          }
        });
      }
    }

    showCustomSizeSummary() {
      const variantPicker = document.querySelector('variant-picker');
      if (!variantPicker) return;

      // Remove existing summary
      const existingSummary = variantPicker.querySelector('.m-custom-size-summary');
      if (existingSummary) {
        existingSummary.remove();
      }

      // Create new summary
      const summary = document.createElement('div');
      summary.className = 'm-custom-size-summary';
      summary.innerHTML = `
        <h4 class="m-custom-size-summary__title">Custom Measurements</h4>
        <ul class="m-custom-size-summary__list">
          <li class="m-custom-size-summary__item">
            <span class="m-custom-size-summary__label">Bust:</span>
            <span class="m-custom-size-summary__value">${this.measurements.bust} cm</span>
          </li>
          <li class="m-custom-size-summary__item">
            <span class="m-custom-size-summary__label">Waist:</span>
            <span class="m-custom-size-summary__value">${this.measurements.waist} cm</span>
          </li>
          <li class="m-custom-size-summary__item">
            <span class="m-custom-size-summary__label">Hips:</span>
            <span class="m-custom-size-summary__value">${this.measurements.hips} cm</span>
          </li>
          <li class="m-custom-size-summary__item">
            <span class="m-custom-size-summary__label">Length from Waist till Heels:</span>
            <span class="m-custom-size-summary__value">${this.measurements['skirt-length']} cm</span>
          </li>
        </ul>

      `;

      // Find size option and append summary
      const sizeOption = Array.from(variantPicker.querySelectorAll('.m-product-option')).find(option => {
        return option.textContent.toLowerCase().includes('size');
      });

      if (sizeOption) {
        sizeOption.appendChild(summary);
      }
    }

    storeCustomSizeData(customSizeString) {
      // Find or create hidden input for custom size data
      const productForm = document.querySelector('product-form form');
      if (!productForm) return;

      // REMOVE any old "Custom Size" property if it exists
      const oldCustomSizeInput = productForm.querySelector('input[name="properties[Custom Size]"]');
      if (oldCustomSizeInput) {
        oldCustomSizeInput.remove();
        console.log('Removed old Custom Size property');
      }

      // Store individual measurements with _Details label
      const measurementKeys = {
        '_Details': 'Custom measurements',
        'Bust': this.measurements.bust,
        'Waist': this.measurements.waist,
        'Hips': this.measurements.hips,
        'Length from Waist till Heels': this.measurements['skirt-length']
      };

      Object.keys(measurementKeys).forEach((key) => {
        let input = productForm.querySelector(`input[name="properties[${key}]"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${key}]`;
          productForm.appendChild(input);
        }
        
        // For _Details, just set the value as is
        // For measurements, append " cm"
        if (key === '_Details') {
          input.value = measurementKeys[key];
        } else {
          input.value = `${measurementKeys[key]} cm`;
        }
      });
      
      console.log('Stored 12 individual measurements (no long string)');
    }
  }

  customElements.define("m-custom-size", CustomSizeModal);
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  // Create custom size element if it doesn't exist
  if (!document.querySelector('m-custom-size')) {
    const customSizeElement = document.createElement('m-custom-size');
    document.body.appendChild(customSizeElement);
  }
});

